import { ContentTypeEnumSchema, MessageParticipantRole, MessageType, ResponseMessage } from '@amzn/ring-cs-chatbot-service-model';
import { SubscriptionContainer } from '../../utils/SubscriptionContainer';
import { ManagedSession } from './ManagedSession';

export type ReceiptData = {
    latestReadTimestamp: number;
    latestReadMessageId: string | null;
    latestDeliveredTimestamp: number;
    latestDeliveredMessageId: string | null;
}

const RECEIPT_CONTENT_TYPES = {
    DELIVERED: ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.message.delivered'],
    READ: ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.message.read'],
};

export type ReceiptContentType = typeof RECEIPT_CONTENT_TYPES[keyof typeof RECEIPT_CONTENT_TYPES];

export class ReceiptManager {
    public readonly receiptContainer: SubscriptionContainer<ReceiptData>;

    private reading: boolean;
    private lastUnreadAgentMessageId: string | null;

    // Map of delivered/read agent message ids to the corresponding timestamps 
    private readonly deliveredAgentMessageMap: Map<string, number>;
    private readonly readAgentMessageMap: Map<string, number>;

    constructor (private session: ManagedSession) {
        this.receiptContainer = new SubscriptionContainer<ReceiptData>({
            latestReadTimestamp: 0,
            latestReadMessageId: null,
            latestDeliveredTimestamp: 0,
            latestDeliveredMessageId: null,
        });
        this.reading = false;

        this.lastUnreadAgentMessageId = null;

        this.deliveredAgentMessageMap = new Map();
        this.readAgentMessageMap = new Map();

        this.session.onMessage({ handler: this.handleMessage.bind(this) });
    }

    private handleMessage (message: ResponseMessage) {
        if (message.participantRole === MessageParticipantRole.AGENT) {
            switch (message.contentType) {
                case ContentTypeEnumSchema.enum['text/plain']:
                case ContentTypeEnumSchema.enum['text/markdown']:
                    {
                        // Send receipts for incoming messages
                        const receiptContentType = this.reading
                            ? RECEIPT_CONTENT_TYPES.READ
                            : RECEIPT_CONTENT_TYPES.DELIVERED;
                        this.sendMessageReceipt(message.messageId, receiptContentType);
                        break;
                    }
                default:
                    break;
            }
        } else if (message.contentType === ContentTypeEnumSchema.enum['application/vnd.amazonaws.connect.event.message.metadata']) {
            // Process incoming receipt events
            this.processCustomerMessageReceipts(message);
        }
    }

    private sendMessageReceipt = async (messageId: string, contentType: ReceiptContentType) => {
        await this.session.sendMessage({
            message: {
                type: MessageType.EVENT,
                contentType,
                content: JSON.stringify({ messageId }),
            },
        });
    };

    /* Updates receiptContainer based on customer message metadata.
     * Message metadata for customer messages is obtained from one of two message types:
     * 1. MessageMetadata events
     * 2. Customer messages from the chat transcript with metadata persisted from previous MessageMetadata events
     */
    private processCustomerMessageReceipts = (...receipts: ResponseMessage[]) => {
        const currentReceiptData = { ...this.receiptContainer.value };
        let receiptDataUpdated = false;

        for (const receiptMessage of receipts) {
            if (!receiptMessage.messageMetadata) continue;

            const md = receiptMessage.messageMetadata;

            const messageId = md.messageId;

            const deliveredTimestamp = md.receipts.find(receipt =>
                receipt.recipientParticipantRole === MessageParticipantRole.AGENT && !!receipt.deliveredTimestamp
            )?.deliveredTimestamp;
            const readTimestamp = md.receipts.find(receipt =>
                receipt.recipientParticipantRole === MessageParticipantRole.AGENT && !!receipt.readTimestamp
            )?.readTimestamp;

            // Process agent receipt data
            if (readTimestamp) {
                // update read receipt if we don't have one yet or if it's newer
                if (!currentReceiptData.latestReadMessageId ||
                    readTimestamp > currentReceiptData.latestReadTimestamp
                ) {
                    currentReceiptData.latestReadTimestamp = readTimestamp;
                    currentReceiptData.latestReadMessageId = messageId;
                    receiptDataUpdated = true;
                }
            }

            if (deliveredTimestamp) {
                // update delivered receipt if we don't have one yet or if it's newer
                if (!currentReceiptData.latestDeliveredMessageId ||
                    deliveredTimestamp > currentReceiptData.latestDeliveredTimestamp
                ) {
                    currentReceiptData.latestDeliveredTimestamp = deliveredTimestamp;
                    currentReceiptData.latestDeliveredMessageId = messageId;
                    receiptDataUpdated = true;
                }
            }
        }

        if (receiptDataUpdated) {
            this.receiptContainer.setValue(currentReceiptData);
        }
    };

    /* Hydrates deliveredAgentMessageMap and readAgentMessageMap based on agent message metadata in the chat transcript.
     * Sends receipt events if required.
     */
    private processAgentMessageReceipts = (messages: ResponseMessage[]) => {
        let lastUndeliveredMessage: ResponseMessage | null = null;
        let lastDeliveredMessage: ResponseMessage | null = null;

        let lastUnreadMessage: ResponseMessage | null = null;
        let lastReadMessage: ResponseMessage | null = null;

        for (const message of messages) {
            // filter to only agent messages
            if (message.type !== MessageType.MESSAGE || message.participantRole !== MessageParticipantRole.AGENT) {
                continue;
            }

            const md = message.messageMetadata;
            const deliveredTimestamp = md?.receipts.find(receipt =>
                receipt.recipientParticipantRole === MessageParticipantRole.CUSTOMER && !!receipt.deliveredTimestamp
            )?.deliveredTimestamp;
            const readTimestamp = md?.receipts.find(receipt =>
                receipt.recipientParticipantRole === MessageParticipantRole.CUSTOMER && !!receipt.readTimestamp
            )?.readTimestamp;

            if (!deliveredTimestamp) {
                // Delivered receipt was not sent
                if (!lastUndeliveredMessage || message.absoluteTime > lastUndeliveredMessage.absoluteTime) {
                    lastUndeliveredMessage = message;
                }
            } else if (deliveredTimestamp && !readTimestamp) {
                // Read receipt was not sent 
                if (!lastUnreadMessage || message.absoluteTime > lastUnreadMessage.absoluteTime) {
                    lastUnreadMessage = message;
                }

                this.deliveredAgentMessageMap.set(message.messageId, deliveredTimestamp);
                if (!lastDeliveredMessage || message.absoluteTime > lastDeliveredMessage.absoluteTime) {
                    lastDeliveredMessage = message;
                }
            } else if (deliveredTimestamp && readTimestamp) {
                // Message was delivered and read
                this.deliveredAgentMessageMap.set(message.messageId, deliveredTimestamp);
                this.readAgentMessageMap.set(message.messageId, readTimestamp);
                if (!lastDeliveredMessage || message.absoluteTime > lastDeliveredMessage.absoluteTime) {
                    lastDeliveredMessage = message;
                }
                if (!lastReadMessage || message.absoluteTime > lastReadMessage.absoluteTime) {
                    lastReadMessage = message;
                }
            }
        }

        /* Due to receipt event throttling some messages may not have receipts.
         * If a newer message with a receipt is found, prior messages without receipts can be ignored. */
        if (lastUndeliveredMessage && lastDeliveredMessage &&
            lastDeliveredMessage.absoluteTime > lastUndeliveredMessage.absoluteTime) {
            lastUndeliveredMessage = null;
        }
        if (lastUnreadMessage && lastReadMessage &&
            lastReadMessage.absoluteTime > lastUnreadMessage.absoluteTime) {
            lastUnreadMessage = null;
        }

        if (this.reading) {
            // Read the last message
            const messageToRead = lastUndeliveredMessage?.messageId || lastUnreadMessage?.messageId;
            if (messageToRead) {
                this.sendMessageReceipt(messageToRead, RECEIPT_CONTENT_TYPES.READ);
            }
        } else {
            // Set lastUnreadAgentMessageId and send delivered receipt if required
            this.lastUnreadAgentMessageId = (lastUndeliveredMessage?.messageId || lastUnreadMessage?.messageId) ?? null;
            if (lastUndeliveredMessage) {
                this.sendMessageReceipt(lastUndeliveredMessage.messageId, RECEIPT_CONTENT_TYPES.DELIVERED);
            }
        }
    };

    private readLastUnreadAgentMessage = async () => {
        if (this.lastUnreadAgentMessageId) {
            await this.sendMessageReceipt(this.lastUnreadAgentMessageId, RECEIPT_CONTENT_TYPES.READ);
            this.lastUnreadAgentMessageId = null;
        }
    };

    hydrateReceipts = (transcript: ResponseMessage[]) => {
        this.processCustomerMessageReceipts(...transcript);
        this.processAgentMessageReceipts(transcript);
    };

    updateReading = (reading: boolean) => {
        // only action if the flag is changing
        if (this.reading !== reading) {
            this.reading = reading;

            // if we're now reading, send appropriate receipts
            if (reading) {
                this.readLastUnreadAgentMessage();
            }
        }
    };
}
